import { useState } from 'react';
import { TextField, TextFieldProps, IconButton } from '@mui/material';
import { Controller } from 'react-hook-form';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

type FormInputProps = {
  control: any;
  name: string;
  rules?: any;
  error?: any;
  secured?: boolean;
};

type Props = FormInputProps &
  TextFieldProps & {
    handleChange?: (name: string, value: string) => void;
  };

/**
 * A form input component that integrates MUI TextField with react-hook-form's Controller.
 * Provides automatic error handling, validation integration, and optional password visibility toggle.
 *
 * This component is the recommended replacement for the deprecated ContolledTextField and InputField components.
 *
 * @param {Object} props - Component props
 * @param {Object} props.control - The react-hook-form control object from useForm()
 * @param {string} props.name - The field name used for react-hook-form registration and error lookup
 * @param {Object} [props.rules] - Validation rules object to pass to react-hook-form Controller
 * @param {Object} [props.error] - External error object (can be used to override fieldState errors)
 * @param {boolean} [props.secured] - If true, renders as a password field with show/hide toggle button
 * @param {Function} [props.handleChange] - Optional callback function called when the input value changes.
 *   Receives the field name and new value as parameters: (name: string, value: string) => void
 * @param {TextFieldProps} [props.textFieldProps] - Additional props to pass through to the underlying MUI TextField
 *   (e.g., label, placeholder, fullWidth, disabled, etc.)
 *
 * @example
 * ```tsx
 * const { control, formState: { errors } } = useForm();
 *
 * // Basic text input
 * <FormInput
 *   name="email"
 *   control={control}
 *   label="Email Address"
 *   rules={{ required: 'Email is required' }}
 *   error={errors.email}
 * />
 *
 * // Password input with visibility toggle
 * <FormInput
 *   name="password"
 *   control={control}
 *   label="Password"
 *   secured
 *   rules={{ required: 'Password is required', minLength: 8 }}
 *   error={errors.password}
 * />
 *
 * // With custom change handler
 * <FormInput
 *   name="username"
 *   control={control}
 *   label="Username"
 *   handleChange={(name, value) => {
 *     console.log(`Field ${name} changed to: ${value}`);
 *   }}
 * />
 * ```
 */
export const FormInput = ({
  control,
  name,
  rules,
  error,
  handleChange: handleChangeProp,
  secured,
  ...textFieldProps
}: Props) => {
  const [showPassword, setShowPassword] = useState(false);

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const inputType = secured && showPassword ? 'text' : 'password';
  const toggleIcon = showPassword ? <VisibilityIcon /> : <VisibilityOffIcon />;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      defaultValue=""
      render={({
        field: { onChange, value },
        fieldState: { error: fieldError },
      }) => {
        const hasError = !!error || !!fieldError;
        const errorMessage = fieldError?.message || null;

        const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const { value } = e.target;
          onChange(value);
          if (handleChangeProp) {
            handleChangeProp(name, value);
          }
        };

        return (
          <TextField
            type={!secured ? 'text' : inputType}
            onChange={handleChange}
            value={value}
            sx={{
              '& .MuiOutlinedInput-root': {
                '& fieldset': {
                  borderRadius: '12px',
                },
                '&.Mui-focused fieldset': {
                  borderRadius: '12px',
                },
                '& input': {
                  borderRadius: '12px',
                },
              },
            }}
            error={hasError}
            helperText={hasError ? errorMessage : ' '}
            {...textFieldProps}
            InputProps={
              secured
                ? {
                    endAdornment: (
                      <IconButton onClick={toggleShowPassword}>
                        {toggleIcon}
                      </IconButton>
                    ),
                  }
                : {}
            }
          />
        );
      }}
    />
  );
};
